import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { api, type MailMessageDetail, type PublicMailAccount } from "../../lib/api";
import { RichMailEditor } from "./RichMailEditor";

export type ComposeMode = "new" | "reply" | "replyAll" | "forward";

function extractEmails(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const found = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of found) {
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function prefixSubject(subject: string, prefix: "Re:" | "Fwd:") {
  const s = (subject || "").trim();
  if (!s) return `${prefix} `;
  if (prefix === "Re:" && /^re:/i.test(s)) return s;
  if (prefix === "Fwd:" && /^(fwd|fw):/i.test(s)) return s;
  return `${prefix} ${s}`;
}

/** Prefer the richest body so reply/forward keep the full quoted thread. */
function messageBodyHtml(source: MailMessageDetail): string {
  const html = (source.html || "").trim();
  const text = (source.text || "").trim();
  const textAsHtml = text
    ? `<div style="white-space:pre-wrap;font-family:inherit">${escapeHtml(text).replace(/\n/g, "<br/>")}</div>`
    : "";
  if (html && textAsHtml) {
    const htmlPlainLen = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().length;
    // If plain text is much longer, it often carries more of the thread.
    if (text.length > htmlPlainLen * 1.8) return textAsHtml;
    return html;
  }
  if (html) return html;
  return textAsHtml || "<p>(messaggio vuoto)</p>";
}

function buildHistoryHtml(mode: ComposeMode, source: MailMessageDetail): string {
  const when = source.date ? new Date(source.date).toLocaleString() : "";
  const body = messageBodyHtml(source);

  if (mode === "forward") {
    return [
      `<div class="ms-forward-history">`,
      `<hr/>`,
      `<p><strong>---------- Messaggio inoltrato ----------</strong></p>`,
      `<p><strong>Da:</strong> ${escapeHtml(source.from || "")}<br/>`,
      `<strong>Data:</strong> ${escapeHtml(when)}<br/>`,
      `<strong>Oggetto:</strong> ${escapeHtml(source.subject || "")}<br/>`,
      `<strong>A:</strong> ${escapeHtml(source.to || "")}`,
      source.cc ? `<br/><strong>Cc:</strong> ${escapeHtml(source.cc)}` : "",
      `</p>`,
      body,
      `</div>`,
    ].join("");
  }

  // reply / replyAll — keep entire original body (includes prior quotes)
  return [
    `<div class="ms-reply-history">`,
    `<blockquote style="margin:0 0 0 0.5rem;padding-left:0.75rem;border-left:3px solid #cbd5e1;color:#475569">`,
    `<p><em>Il ${escapeHtml(when)} ${escapeHtml(source.from || "")} ha scritto:</em></p>`,
    body,
    `</blockquote>`,
    `</div>`,
  ].join("");
}

function buildComposeDraft(
  mode: ComposeMode,
  source: MailMessageDetail | null,
  selfEmails: string[],
): {
  to: string;
  cc: string;
  subject: string;
  title: string;
  historyHtml: string;
} {
  const self = new Set(selfEmails.map((e) => e.toLowerCase()));
  if (!source || mode === "new") {
    return { to: "", cc: "", subject: "", title: "Nuovo messaggio", historyHtml: "" };
  }

  if (mode === "forward") {
    return {
      to: "",
      cc: "",
      subject: prefixSubject(source.subject, "Fwd:"),
      title: "Inoltra",
      historyHtml: buildHistoryHtml("forward", source),
    };
  }

  const fromEmails = extractEmails(source.from);
  const toEmails = extractEmails(source.to);
  const ccEmails = extractEmails(source.cc);
  const historyHtml = buildHistoryHtml(mode, source);

  if (mode === "reply") {
    return {
      to: fromEmails.join(", "),
      cc: "",
      subject: prefixSubject(source.subject, "Re:"),
      title: "Rispondi",
      historyHtml,
    };
  }

  const primary = fromEmails[0] ? [fromEmails[0]] : [];
  const ccSet = new Set<string>();
  for (const e of [...fromEmails.slice(1), ...toEmails, ...ccEmails]) {
    const key = e.toLowerCase();
    if (self.has(key)) continue;
    if (primary.some((p) => p.toLowerCase() === key)) continue;
    ccSet.add(e);
  }
  return {
    to: primary.join(", "),
    cc: [...ccSet].join(", "),
    subject: prefixSubject(source.subject, "Re:"),
    title: "Rispondi a tutti",
    historyHtml,
  };
}

export function ComposerModal({
  accounts,
  defaultAccountId,
  mode,
  source,
  onClose,
  onSent,
}: {
  accounts: PublicMailAccount[];
  defaultAccountId?: string;
  mode: ComposeMode;
  source: MailMessageDetail | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const accountIdInit = defaultAccountId || accounts[0]?.id || "";
  const selfEmails = useMemo(() => accounts.map((a) => a.email), [accounts]);
  const draft = useMemo(
    () => buildComposeDraft(mode, source, selfEmails),
    [mode, source, selfEmails],
  );

  const [accountId, setAccountId] = useState(accountIdInit);
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(Boolean(draft.cc) || mode === "replyAll");
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(draft.subject);
  /** Only the new text the user types — history stays outside TipTap. */
  const [bodyHtml, setBodyHtml] = useState("<p></p>");
  const [historyOpen, setHistoryOpen] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [draftTick, setDraftTick] = useState(0);
  const [suggestions, setSuggestions] = useState<Array<{ displayName: string; email: string }>>([]);

  const safeHistory = useMemo(() => {
    if (!draft.historyHtml) return "";
    return DOMPurify.sanitize(draft.historyHtml, { USE_PROFILES: { html: true } });
  }, [draft.historyHtml]);

  useEffect(() => {
    const t = window.setInterval(() => setDraftTick((n) => n + 1), 15000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!draftTick) return;
    localStorage.setItem(
      "mailstorm.draft",
      JSON.stringify({
        accountId,
        to,
        cc,
        bcc,
        subject,
        bodyHtml,
        historyHtml: draft.historyHtml,
        mode,
        at: Date.now(),
      }),
    );
  }, [draftTick, accountId, to, cc, bcc, subject, bodyHtml, draft.historyHtml, mode]);

  useEffect(() => {
    const q = to.split(",").pop()?.trim() || "";
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void api.suggestContacts(q).then((r) => setSuggestions(r.contacts)).catch(() => setSuggestions([]));
    }, 180);
    return () => window.clearTimeout(handle);
  }, [to]);

  async function send() {
    if (!accountId) {
      setError("Scegli un account mittente");
      return;
    }
    const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      setError("Inserisci almeno un destinatario in A");
      return;
    }
    setSending(true);
    setError("");
    try {
      const html = `${bodyHtml}${draft.historyHtml ? `<br/>${draft.historyHtml}` : ""}`;
      const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      await api.send({
        accountId,
        to: recipients,
        cc: cc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        bcc: bcc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        subject,
        html,
        text,
        inReplyTo: mode === "forward" || mode === "new" ? undefined : source?.messageId || undefined,
        references: mode === "forward" || mode === "new" ? undefined : source?.messageId || undefined,
      });
      localStorage.removeItem("mailstorm.draft");
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal modal-composer" onClick={(e) => e.stopPropagation()}>
        <div className="composer-chrome">
          <div>
            <div className="eyebrow">Compose</div>
            <strong className="composer-title">{draft.title}</strong>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Chiudi" type="button">
            ✕
          </button>
        </div>
        <div className="composer">
          <div className="composer-fields">
            <div className="composer-field">
              <label className="label">Da</label>
              <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="composer-field">
              <label className="label">A</label>
              <input
                className="input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                autoComplete="off"
                placeholder={mode === "forward" ? "Destinatario dell’inoltro" : undefined}
                autoFocus={mode === "forward"}
              />
              {suggestions.length > 0 && (
                <div className="suggest-list">
                  {suggestions.map((s) => (
                    <button
                      key={s.email}
                      type="button"
                      className="suggest-item"
                      onClick={() => {
                        const parts = to.split(",");
                        parts[parts.length - 1] = ` ${s.email}`;
                        setTo(parts.join(",").replace(/^\s+/, ""));
                        setSuggestions([]);
                      }}
                    >
                      {s.displayName} · {s.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="composer-field-row">
              {!showCc ? (
                <button type="button" className="btn btn-ghost" onClick={() => setShowCc(true)}>
                  Cc
                </button>
              ) : (
                <div className="composer-field" style={{ flex: 1 }}>
                  <label className="label">Cc</label>
                  <input className="input" value={cc} onChange={(e) => setCc(e.target.value)} />
                </div>
              )}
              <button type="button" className="btn btn-ghost" onClick={() => setShowBcc((v) => !v)}>
                {showBcc ? "Nascondi Ccn" : "Ccn"}
              </button>
            </div>
            {showBcc && (
              <div className="composer-field">
                <label className="label">Ccn</label>
                <input className="input" value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
            )}
            <div className="composer-field">
              <label className="label">Oggetto</label>
              <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          </div>

          <RichMailEditor
            key={`${mode}-${source?.uid || "new"}-${source?.accountId || ""}`}
            initialHtml="<p></p>"
            onChange={setBodyHtml}
          />

          {safeHistory ? (
            <div className="composer-history">
              <button
                type="button"
                className="composer-history-toggle"
                onClick={() => setHistoryOpen((v) => !v)}
              >
                {historyOpen ? "▾" : "▸"}{" "}
                {mode === "forward" ? "Messaggio inoltrato (storico completo)" : "Cronologia citata (storico completo)"}
              </button>
              {historyOpen && (
                <iframe
                  className="composer-history-frame"
                  title="cronologia messaggio"
                  sandbox=""
                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"/><style>body{font-family:IBM Plex Sans,Segoe UI,sans-serif;color:#334155;line-height:1.5;margin:12px 14px;font-size:13px;background:#f8fafc}img{max-width:100%;height:auto}blockquote{margin-left:0.4rem;padding-left:0.7rem;border-left:3px solid #cbd5e1}</style></head><body>${safeHistory}</body></html>`}
                />
              )}
            </div>
          ) : null}

          <div className="composer-footer">
            <span className="composer-hint">
              {mode === "forward"
                ? "Inoltro: inserisci i destinatari · lo storico resta allegato sotto"
                : mode === "new"
                  ? "Bozza locale ogni 15s"
                  : "Lo storico completo resta sotto e viene inviato con la risposta"}
            </span>
            {error && <div className="error">{error}</div>}
            <div className="composer-actions">
              <button className="btn" type="button" onClick={onClose}>
                Annulla
              </button>
              <button className="btn btn-primary" type="button" onClick={() => void send()} disabled={sending}>
                {sending ? "Invio…" : mode === "forward" ? "Inoltra" : "Invia"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
